import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Paper, Typography, CircularProgress, Alert } from '@mui/material';
import { academyApi } from '../../services/academyApi';

export const PortalSsoCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const query = new URLSearchParams(location.search);
    const code = query.get('code') || query.get('token') || '';
    const stateStr = query.get('state') || '';
    const error = query.get('error') || query.get('error_description');

    let portalType: 'student' | 'teacher' = 'student';
    let provider = 'google';

    if (stateStr) {
      try {
        const decoded = JSON.parse(atob(stateStr));
        if (decoded.portalType === 'teacher') portalType = 'teacher';
        if (decoded.provider) provider = decoded.provider;
      } catch (e) {
        console.warn('Could not decode state in portal SSO callback:', e);
      }
    }

    const fallbackUrl = portalType === 'teacher' ? '/portal/teacher' : '/portal/student';

    if (error) {
      setErrorMsg(error);
      setLoading(false);
      setTimeout(() => navigate(fallbackUrl, { replace: true }), 3000);
      return;
    }

    if (!code) {
      setErrorMsg('Code d\'autorisation SSO manquant.');
      setLoading(false);
      setTimeout(() => navigate(fallbackUrl, { replace: true }), 3000);
      return;
    }

    // Exchange SSO code/token with backend
    academyApi.portalSsoVerify(provider, code, portalType)
      .then((res) => {
        const ssoToken = res?.token || res?.data?.token;
        if (res && res.success && ssoToken) {
          const storageKey = portalType === 'teacher'
            ? 'blobtic_portal_teacher_token'
            : 'blobtic_portal_student_token';
          localStorage.setItem(storageKey, ssoToken);
          navigate(fallbackUrl, { replace: true });
        } else {
          setErrorMsg(res?.error || 'Échec de l\'authentification SSO.');
          setLoading(false);
          setTimeout(() => navigate(fallbackUrl, { replace: true }), 3500);
        }
      })
      .catch((err) => {
        console.error('Portal SSO verify error:', err);
        setErrorMsg('Erreur lors de la validation du compte SSO.');
        setLoading(false);
        setTimeout(() => navigate(fallbackUrl, { replace: true }), 3500);
      });
  }, [location.search, navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        bgcolor: '#f8fafc'
      }}
    >
      <Paper sx={{ p: 4, borderRadius: 3, maxWidth: 420, width: '100%', textAlign: 'center' }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress color="primary" />
            <Typography variant="body1" fontWeight={600}>
              Validation de votre connexion SSO en cours...
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Veuillez patienter pendant la vérification de vos accès.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {errorMsg}
            </Alert>
            <Typography variant="caption" color="text.secondary">
              Redirection automatique vers le portail...
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default PortalSsoCallbackPage;
